import { flex } from 'styles/utils';
import { cx } from '@linaria/core';
import { Button } from 'components/ui_library/Button';
import { either } from 'fp-ts';
import { Formik, Form } from 'formik';
import { InputField } from 'components/ui_library/Formik';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { errorToReactLeft, pipe } from 'utils/fp';
import { reactLeftToJSX } from 'utils/uiStates/uiStates';
import { useCallback, useState } from 'react';
import { fetchUserApiScopesTE } from 'api/keys/fetchUserApiScopes';
import { getScopeAndEndpoint } from 'modules/Apps/deprecatedScopeGraphLogic/scope';
import { ScopesList } from 'modules/Apps/components/ScopesList';
import { TUserKeysMST, KeyCreationEventType } from 'modules/Apps/mstStores/UserKeysMST';
import { modalKeys, APIKeyType, PATKeyType } from 'modules/AppSettings/ApiKeysTable/constants';
import { useModalActions } from 'components/ModalManager/Context';
import { dialogWrapper, formContainer } from './styles';

type Props = {
  store: TUserKeysMST;
  onClose: () => void;
  callback?: (eventType: KeyCreationEventType) => void;
} & (
  | {
      type: typeof APIKeyType;
      userId: string;
      appId: string;
    }
  | {
      type: typeof PATKeyType;
      userId: string;
    }
);

interface FormData {
  tokenDescription: string;
}

export const KEY_NAMES: Record<CF.API.ApiKeys.KeyType, string> = { API_KEY: 'API key', PAT: 'Personal Access Token' };
const PAT_KEY_VALUE = 'personal_access_token' as const;

export const AddApiKeyModal = ({ store, onClose, type, userId, callback, ...rest }: Props): JSX.Element => {
  const { closeModal } = useModalActions();
  const modalId = 'appId' in rest ? modalKeys.apiKey.add : modalKeys.patKey.add;
  const keyType = 'appId' in rest ? 'API_KEY' : 'PAT';
  const { data: scopesTE } = useQueryTE([queryKeys.Scopes, { userId, keyType }], fetchUserApiScopesTE({ userId, keyType }, errorToReactLeft));
  const [scopesEntity, setScopesEntity] = useState<string[]>([]);

  const onScopesChanged = useCallback((newScopes: string[]): void => {
    setScopesEntity(newScopes);
  }, []);

  const handleCallback = useCallback(
    (eventType: KeyCreationEventType) => {
      if (eventType === KeyCreationEventType.SUCCESS) {
        onClose();
      }

      if (callback) {
        callback(eventType);
      }
    },
    [callback, onClose],
  );

  const onSubmit = useCallback(
    (values) => {
      const { scopes, endpoints } = getScopeAndEndpoint(scopesEntity);
      store.createNewKey(
        /* istanbul ignore next: TypeScript type narrowing */
        'appId' in rest
          ? {
              // api key
              userId,
              appId: rest.appId,
              scopes,
              endpoints,
              description: values.tokenDescription,
              callback: handleCallback,
            }
          : {
              // PAT
              type: PAT_KEY_VALUE,
              userId,
              scopes,
              endpoints,
              description: values.tokenDescription,
              callback: handleCallback,
            },
      );
    },
    [store, userId, scopesEntity, onClose, (rest as { appId: string }).appId],
  );

  return (
    <div className={dialogWrapper}>
      <div>
        <p className="modal-description">
          Use the following form to generate a new {KEY_NAMES[type].toLowerCase()} to represent you in the APIs for all your apps and any apps you’ve
          been invited to access.{' '}
          <a
            href="https://docs.clarifai.com/clarifai-basics/authentication/personal-access-tokens"
            rel="noreferrer"
            target="_blank"
            className="learn-more"
          >
            Learn more
          </a>
        </p>
      </div>

      <div className={formContainer}>
        <Formik initialValues={{ tokenDescription: '' } as FormData} onSubmit={onSubmit}>
          <Form>
            <div>
              <div className="form-field">
                <div className="field-name">Token Description</div>
                <InputField type="text" placeholder="Short descriptive name" name="tokenDescription" />
              </div>

              {pipe(
                scopesTE,
                either.fold(reactLeftToJSX(), ({ scope_deps, endpoint_deps }) => (
                  <ScopesList scopeDeps={scope_deps} endpointDeps={endpoint_deps} onScopesChanged={onScopesChanged} />
                )),
              )}

              <div className={cx(flex.init, flex.gap[1], flex.justifyContent.end)}>
                <Button
                  variant="tertiary"
                  onClick={() => {
                    handleCallback(KeyCreationEventType.CANCELLED);
                    closeModal({ id: modalId });
                  }}
                >
                  Cancel
                </Button>
                <Button data-testid="create-key-button" type="submit" disabled={scopesEntity.length === 0}>
                  {`Create ${KEY_NAMES[type]}`}
                </Button>
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};
